// Q9 S5 — Auto-save state machine + retry queue + debounce dispatcher.
//
// This is the trust layer on top of S3's PATCH-on-step-nav data flow.
// S3 wired the carer's primary "Next" trigger (D10 step-nav). S5 adds
// the secondary triggers (D10 textarea-blur + debounced-numeric) and
// the failure-recovery surface (D11 badge + D12 allow-nav-with-retry).
//
// Locks honored:
//   D10 — combination trigger: step-nav (S3) + textarea-blur + debounced
//         numerics. The hook exposes three dispatch entry points; the
//         step-nav path keeps using the wizard's existing handler.
//   D11 — real-timestamp badge. Each successful PATCH bumps
//         lastSavedAt[section]; the badge component reads from it.
//   D12 — allow-nav with retry queue. The wizard's step nav no longer
//         awaits PATCH success; the hook dispatches in the background
//         and reports state via `status`. Retry policy:
//           - Network / 5xx: exponential backoff 1s → 2s → 4s → 8s,
//             capped at 4 attempts; 5th attempt failure persists to IDB
//             via offline-queue-sections.ts and surfaces a manual-retry
//             affordance.
//           - 422 (validation): NO retry — the data is invalid; surface
//             via status.failedSections so RetryIndicator can show it.
//           - 403 / 404 / 409: NO retry — modal/redirect handled by the
//             wizard at a higher layer; the hook just records the
//             failure (no retry timers fire).
//   D13 — offline replay handled by useOfflineSync (extended in S5);
//         this hook just persists irrecoverable failures to the queue.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api.js";
import { patchSection, type Fetcher } from "../api/visit-report-api.js";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";
import { enqueueSectionPatch } from "../lib/offline-queue-sections.js";

export const MAX_RETRY_ATTEMPTS = 4;
const RETRY_BACKOFF_MS = [1000, 2000, 4000, 8000];   // attempt 1→2→3→4→5
const DEFAULT_DEBOUNCE_MS = 750;                      // spec D10: ~750ms after last keystroke

export interface FailedSection {
  attemptCount: number;
  lastError: string;
  /** True once the in-memory retry budget is exhausted; the entry has been
   *  persisted to IDB and the carer needs to manually retry or wait for
   *  the offline-sync replay. */
  exhausted: boolean;
  /** True for non-retryable errors (422 validation, 403/404/409). */
  permanent: boolean;
}

export interface AutoSaveStatus {
  lastSavedAt: Partial<Record<SectionStep, Date>>;
  pendingSections: ReadonlySet<SectionStep>;
  failedSections: ReadonlyMap<SectionStep, FailedSection>;
}

const EMPTY_STATUS: AutoSaveStatus = {
  lastSavedAt: {},
  pendingSections: new Set(),
  failedSections: new Map(),
};

/** Categorise an HTTP status: should we retry, or surface immediately? */
function isRetryableError(err: unknown): boolean {
  if (err instanceof ApiError) {
    // 422 = validation; 4xx other than 408/429 = client error, do not retry.
    if (err.status >= 400 && err.status < 500) {
      return err.status === 408 || err.status === 429;
    }
    // 5xx = transient; retry.
    return err.status >= 500;
  }
  // Network errors (no Response): treat as retryable.
  return true;
}

function formatErrMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "Save failed";
}

export interface UseAutoSaveResult {
  status: AutoSaveStatus;
  /** Save a section's payload; the hook tracks success/failure + retries. */
  dispatchSave: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>;
  /** Convenience for textarea blur — same as dispatchSave but named for callsites. */
  dispatchFieldBlur: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>;
  /** Debounced dispatch for numeric inputs; coalesces rapid changes per section. */
  dispatchDebouncedNumeric: (section: SectionStep, payload: Record<string, unknown>) => void;
  /** Manual retry from the RetryIndicator's "Retry" button; resets attempt counter. */
  manualRetry: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>;
}

interface InternalState {
  attemptCount: Map<SectionStep, number>;
  retryTimers: Map<SectionStep, ReturnType<typeof setTimeout>>;
  debounceTimers: Map<SectionStep, ReturnType<typeof setTimeout>>;
  /** Latest payload per section — used for the debounce timer's eventual fire. */
  pendingDebouncedPayloads: Map<SectionStep, Record<string, unknown>>;
  cancelled: boolean;
}

interface StatusUpdaters {
  status: AutoSaveStatus;
  markPending: (section: SectionStep) => void;
  markSaved: (section: SectionStep) => void;
  markFailed: (section: SectionStep, info: FailedSection) => void;
}

function useAutoSaveStatus(onSavedClearAttempts: (s: SectionStep) => void): StatusUpdaters {
  const [status, setStatus] = useState<AutoSaveStatus>(EMPTY_STATUS);
  const markPending = useCallback((section: SectionStep): void => {
    setStatus((s) => {
      const next = new Set(s.pendingSections);
      next.add(section);
      return { ...s, pendingSections: next };
    });
  }, []);
  const markSaved = useCallback((section: SectionStep): void => {
    setStatus((s) => {
      const failed = new Map(s.failedSections);
      failed.delete(section);
      const pending = new Set(s.pendingSections);
      pending.delete(section);
      return {
        lastSavedAt: { ...s.lastSavedAt, [section]: new Date() },
        pendingSections: pending,
        failedSections: failed,
      };
    });
    onSavedClearAttempts(section);
  }, [onSavedClearAttempts]);
  const markFailed = useCallback((section: SectionStep, info: FailedSection): void => {
    setStatus((s) => {
      const failed = new Map(s.failedSections);
      failed.set(section, info);
      const pending = new Set(s.pendingSections);
      pending.delete(section);
      return { ...s, pendingSections: pending, failedSections: failed };
    });
  }, []);
  return { status, markPending, markSaved, markFailed };
}

export interface UseAutoSaveOptions {
  /** Carer-parity S3a — wizard threads its useFetcher() so the carer subtree
   *  uses carerFetch (silent refresh on 401) rather than apiFetch. */
  fetcher?: Fetcher;
  debounceMs?: number;
}

export function useAutoSave(
  srId: string, options: UseAutoSaveOptions = {},
): UseAutoSaveResult {
  const { fetcher, debounceMs = DEFAULT_DEBOUNCE_MS } = options;
  const internalRef = useRef<InternalState>({
    attemptCount: new Map(),
    retryTimers: new Map(),
    debounceTimers: new Map(),
    pendingDebouncedPayloads: new Map(),
    cancelled: false,
  });

  const onSavedClearAttempts = useCallback((section: SectionStep): void => {
    internalRef.current.attemptCount.delete(section);
  }, []);
  const { status, markPending, markSaved, markFailed } = useAutoSaveStatus(onSavedClearAttempts);

  // Persist the failed PATCH to the offline-sync IDB queue (best-effort —
  // a missing IDB just falls back to the in-memory retry-button affordance).
  const persistExhausted = useCallback(async (
    section: SectionStep, payload: Record<string, unknown>, attempts: number,
  ): Promise<void> => {
    try {
      await enqueueSectionPatch({
        sr_id: srId, section, payload,
        queued_at: new Date().toISOString(), retry_count: attempts,
      });
    } catch { /* IDB unavailable — fall back to manual retry */ }
  }, [srId]);

  // Decide what to do with a thrown error from patchSection. Splits the
  // permanent / exhausted / retry branches so dispatchSave below stays
  // a thin orchestrator.
  const handleSaveFailure = useCallback(async (
    section: SectionStep, payload: Record<string, unknown>, err: unknown,
    retryCallback: (s: SectionStep, p: Record<string, unknown>) => void,
  ): Promise<void> => {
    const message = formatErrMessage(err);
    const attempts = (internalRef.current.attemptCount.get(section) ?? 0) + 1;
    internalRef.current.attemptCount.set(section, attempts);

    if (!isRetryableError(err)) {
      markFailed(section, { attemptCount: attempts, lastError: message, exhausted: false, permanent: true });
      return;
    }
    if (attempts >= MAX_RETRY_ATTEMPTS) {
      await persistExhausted(section, payload, attempts);
      markFailed(section, { attemptCount: attempts, lastError: message, exhausted: true, permanent: false });
      return;
    }
    markFailed(section, { attemptCount: attempts, lastError: message, exhausted: false, permanent: false });
    const delay = RETRY_BACKOFF_MS[Math.min(attempts - 1, RETRY_BACKOFF_MS.length - 1)] ?? 8000;
    const timer = setTimeout(() => {
      internalRef.current.retryTimers.delete(section);
      retryCallback(section, payload);
    }, delay);
    internalRef.current.retryTimers.set(section, timer);
  }, [markFailed, persistExhausted]);

  const dispatchSave = useCallback(async (
    section: SectionStep, payload: Record<string, unknown>,
  ): Promise<void> => {
    if (internalRef.current.cancelled) return;
    const existingTimer = internalRef.current.retryTimers.get(section);
    if (existingTimer != null) {
      clearTimeout(existingTimer);
      internalRef.current.retryTimers.delete(section);
    }
    markPending(section);
    try {
      // Pass `fetcher` only when the caller provided one — keeps the 3-arg
      // shape stable for offline-replay + existing test mocks.
      if (fetcher) await patchSection(srId, section, payload, fetcher);
      else await patchSection(srId, section, payload);
      if (internalRef.current.cancelled) return;
      markSaved(section);
    } catch (err) {
      if (internalRef.current.cancelled) return;
      await handleSaveFailure(section, payload, err, (s, p) => { void dispatchSave(s, p); });
    }
  }, [srId, fetcher, markPending, markSaved, handleSaveFailure]);

  const triggers = useDispatchTriggers(internalRef, dispatchSave, debounceMs);
  useTeardownTimers(internalRef);

  return { status, dispatchSave, ...triggers };
}

interface DispatchTriggers {
  dispatchFieldBlur: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>;
  dispatchDebouncedNumeric: (section: SectionStep, payload: Record<string, unknown>) => void;
  manualRetry: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>;
}

function useDispatchTriggers(
  internalRef: React.MutableRefObject<InternalState>,
  dispatchSave: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>,
  debounceMs: number,
): DispatchTriggers {
  const dispatchFieldBlur = useCallback((
    section: SectionStep, payload: Record<string, unknown>,
  ): Promise<void> => {
    // Cancel any pending debounce for this section — the blur is the
    // canonical "I'm done editing" signal.
    const debounceTimer = internalRef.current.debounceTimers.get(section);
    if (debounceTimer != null) {
      clearTimeout(debounceTimer);
      internalRef.current.debounceTimers.delete(section);
      internalRef.current.pendingDebouncedPayloads.delete(section);
    }
    return dispatchSave(section, payload);
  }, [internalRef, dispatchSave]);

  const dispatchDebouncedNumeric = useCallback((
    section: SectionStep, payload: Record<string, unknown>,
  ): void => {
    internalRef.current.pendingDebouncedPayloads.set(section, payload);
    const existing = internalRef.current.debounceTimers.get(section);
    if (existing != null) clearTimeout(existing);
    const timer = setTimeout(() => {
      internalRef.current.debounceTimers.delete(section);
      const latest = internalRef.current.pendingDebouncedPayloads.get(section);
      internalRef.current.pendingDebouncedPayloads.delete(section);
      if (latest) void dispatchSave(section, latest);
    }, debounceMs);
    internalRef.current.debounceTimers.set(section, timer);
  }, [internalRef, dispatchSave, debounceMs]);

  const manualRetry = useCallback((
    section: SectionStep, payload: Record<string, unknown>,
  ): Promise<void> => {
    internalRef.current.attemptCount.delete(section);
    return dispatchSave(section, payload);
  }, [internalRef, dispatchSave]);

  return { dispatchFieldBlur, dispatchDebouncedNumeric, manualRetry };
}

// Tear down all timers on unmount so the wizard doesn't leak retries
// after the carer navigates away.
function useTeardownTimers(internalRef: React.MutableRefObject<InternalState>): void {
  useEffect(() => {
    const internal = internalRef.current;
    return () => {
      internal.cancelled = true;
      for (const t of internal.retryTimers.values()) clearTimeout(t);
      for (const t of internal.debounceTimers.values()) clearTimeout(t);
      internal.retryTimers.clear();
      internal.debounceTimers.clear();
    };
  }, [internalRef]);
}
